import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import * as monaco from 'monaco-editor';
import { loader } from '@monaco-editor/react';
import Editor from '@monaco-editor/react';

import { Input, Icon } from 'antd';
import { XPathMappingRow as XPathMappingRowStyled, XPathEditorCell } from './styles';
import VariableMentionPopup from '../EmailDesignPage/EmailBuilder/VariableMentionPopup';
import { isFocusInsideVariableMentionUI } from '../../utils/variableMentionFocus';
import { BUSINESS_CONTRACTS as trans } from './translationValues';
import { registerPdsXpath, PDS_XPATH_LANGUAGE_ID } from './pdsXpathLanguage';

loader.config({ monaco });

const MIN_EDITOR_H = 32;
const MAX_EDITOR_H = 320;

const XPathMappingRowComponent = ({
  index,
  entry,
  pathTree,
  onXPathChange,
  onVariableChange,
  onDelete,
  t,
  globalVariables = [],
  updateGlobalVariables,
  relationships = [],
}) => {
  const [currentXPathKey, setCurrentXPathKey] = useState(entry.xpath);
  const [xpathEditorHeight, setXpathEditorHeight] = useState(48);
  const [popupPosition, setPopupPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const variableInputRef = useRef(null);
  const previousValueRef = useRef(entry.variable);
  const xpathExprRef = useRef(entry.xpath);
  const editorRef = useRef(null);
  const pathTreeRef = useRef(pathTree);
  const cellRef = useRef(null);
  const disposablesRef = useRef([]);
  const indexRef = useRef(index);

  pathTreeRef.current = pathTree;
  indexRef.current = index;

  useEffect(() => {
    setCurrentXPathKey(entry.xpath);
  }, [entry.xpath]);

  useEffect(() => {
    xpathExprRef.current = entry.xpath;
  }, [entry.xpath]);

  useEffect(() => {
    previousValueRef.current = entry.variable;
  }, [entry.variable]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const model = editor.getModel();
    if (model) model.__pdsTree = pathTree;
  }, [pathTree]);

  useEffect(() => {
    return () => {
      disposablesRef.current.forEach((d) => {
        try {
          if (d && typeof d.dispose === 'function') d.dispose();
        } catch (e) {
          /* noop */
        }
      });
      disposablesRef.current = [];
    };
  }, []);

  const applyContentHeight = useCallback((editor) => {
    if (!editor || !cellRef.current) return;
    const raw = editor.getContentHeight();
    const next = Math.max(MIN_EDITOR_H, Math.min(MAX_EDITOR_H, raw));
    setXpathEditorHeight(next);
    cellRef.current.style.height = `${next}px`;
    requestAnimationFrame(() => {
      if (!cellRef.current || !editorRef.current) return;
      editorRef.current.layout({
        width: cellRef.current.clientWidth,
        height: next,
      });
    });
  }, []);

  const handleXPathEditorMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    registerPdsXpath(monacoInstance);
    const model = editor.getModel();
    if (model) {
      model.__pdsTree = pathTreeRef.current;
    }

    applyContentHeight(editor);

    disposablesRef.current.push(
      editor.onDidContentSizeChange(() => {
        applyContentHeight(editor);
      })
    );

    disposablesRef.current.push(
      editor.onDidFocusEditorText(() => {
        editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
      })
    );

    disposablesRef.current.push(
      editor.onDidBlurEditorText(() => {
        const v = editor.getValue();
        const committed = xpathExprRef.current;
        const idx = indexRef.current;
        setCurrentXPathKey(v);
        if (v !== committed) {
          onXPathChange(idx, v);
        }
      })
    );
  };

  const handleXPathEditorChange = (value) => {
    setCurrentXPathKey(value ?? '');
  };

  const POPUP_GAP = 4;
  const popupWrapperRef = useRef(null);

  const computePopupPosFromTarget = useCallback((targetEl) => {
    if (!targetEl) return null;
    const rect = targetEl.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.bottom + POPUP_GAP,
      target: targetEl,
      placeAbove: false,
      measured: false,
    };
  }, []);

  const updatePopupFromTarget = useCallback(() => {
    setPopupPosition((prev) => {
      if (!prev || !prev.target || !prev.target.isConnected) return prev;
      const rect = prev.target.getBoundingClientRect();
      const wrapper = popupWrapperRef.current;
      const popupHeight = wrapper ? wrapper.offsetHeight : 0;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placeAbove =
        popupHeight > 0 &&
        spaceBelow < popupHeight + POPUP_GAP &&
        rect.top > popupHeight + POPUP_GAP;
      const top = placeAbove
        ? Math.max(POPUP_GAP, rect.top - popupHeight - POPUP_GAP)
        : rect.bottom + POPUP_GAP;
      return {
        ...prev,
        left: rect.left,
        top,
        placeAbove,
        measured: popupHeight > 0,
      };
    });
  }, []);

  useLayoutEffect(() => {
    if (!popupPosition || !popupPosition.target) return;
    const wrapper = popupWrapperRef.current;
    if (!wrapper) return;
    const popupHeight = wrapper.offsetHeight;
    if (!popupHeight) return;
    const rect = popupPosition.target.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove =
      spaceBelow < popupHeight + POPUP_GAP &&
      rect.top > popupHeight + POPUP_GAP;
    const desiredTop = placeAbove
      ? Math.max(POPUP_GAP, rect.top - popupHeight - POPUP_GAP)
      : rect.bottom + POPUP_GAP;
    if (
      Math.abs((popupPosition.top || 0) - desiredTop) > 0.5 ||
      popupPosition.placeAbove !== placeAbove ||
      !popupPosition.measured
    ) {
      setPopupPosition((prev) =>
        prev ? { ...prev, top: desiredTop, placeAbove, measured: true } : prev
      );
    }
  });

  const handleVariableInputChange = (e) => {
    const value = e.target.value;
    onVariableChange(indexRef.current, value);
    setSearchTerm(value);
    setPopupPosition(computePopupPosFromTarget(e.target));
  };

  const handleVariableInputClick = (e) => {
    e.stopPropagation();
    previousValueRef.current = e.target.value;
    setSearchTerm(e.target.value);
    setPopupPosition(computePopupPosFromTarget(e.target));
  };

  const handleVariableInputBlur = () => {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (isFocusInsideVariableMentionUI(active)) return;
      setPopupPosition(null);
    });
  };

  useEffect(() => {
    const target = popupPosition && popupPosition.target;
    if (!target) return undefined;
    const handler = () => updatePopupFromTarget();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [popupPosition && popupPosition.target, updatePopupFromTarget]);

  const handleVariableSelect = (variable) => {
    if (variable && variable.placeHolderText) {
      onVariableChange(indexRef.current, variable.placeHolderText);
      setPopupPosition(null);
    }
  };

  const handlePopupHide = () => {
    setPopupPosition(null);
  };

  const rowUuid = `xpath-row-${entry.id}`;

  return (
    <XPathMappingRowStyled>
      <Input
        ref={variableInputRef}
        value={entry.variable || ''}
        onChange={handleVariableInputChange}
        onClick={handleVariableInputClick}
        onBlur={handleVariableInputBlur}
        placeholder={t(trans.VARIABLE_PLACEHOLDER.key, trans.VARIABLE_PLACEHOLDER.value)}
        style={{ flex: 1 }}
      />
      <XPathEditorCell ref={cellRef} style={{ flex: 1, height: xpathEditorHeight }}>
        <Editor
          height={xpathEditorHeight}
          width="100%"
          language={PDS_XPATH_LANGUAGE_ID}
          theme="vs"
          value={currentXPathKey}
          onChange={handleXPathEditorChange}
          onMount={handleXPathEditorMount}
          loading={<span style={{ padding: 8, fontSize: 12 }}>…</span>}
          options={{
            minimap: { enabled: false },
            lineNumbers: 'off',
            glyphMargin: false,
            folding: false,
            scrollBeyondLastLine: false,
            scrollBeyondLastColumn: 0,
            overviewRulerLanes: 0,
            overviewRulerBorder: false,
            hideCursorInOverviewRuler: true,
            renderLineHighlight: 'none',
            fontSize: 13,
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            wordWrap: 'on',
            padding: { top: 6, bottom: 6 },
            automaticLayout: true,
            fixedOverflowWidgets: true,
            quickSuggestions: { other: true, comments: false, strings: true },
            suggestOnTriggerCharacters: true,
            tabSize: 2,
            scrollbar: {
              vertical: 'hidden',
              horizontal: 'hidden',
              handleMouseWheel: false,
              alwaysConsumeMouseWheel: false,
              useShadows: false,
              verticalScrollbarSize: 0,
              horizontalScrollbarSize: 0,
            },
          }}
        />
      </XPathEditorCell>
      {popupPosition && ReactDOM.createPortal(
        <div
          ref={popupWrapperRef}
          style={{
            position: 'fixed',
            left: `${popupPosition.left}px`,
            top: `${popupPosition.top}px`,
            zIndex: 10000,
            visibility: popupPosition.measured ? 'visible' : 'hidden',
          }}
        >
          <VariableMentionPopup
            uuid={rowUuid}
            searchTerm={searchTerm}
            onVariableSelect={handleVariableSelect}
            onHide={handlePopupHide}
            popupPlacement="bottom"
            target={popupPosition.target}
            globalVariables={globalVariables}
            updateGlobalVariables={updateGlobalVariables}
            relationships={relationships}
          />
        </div>,
        document.body
      )}
      <Icon type="delete" onClick={() => onDelete(indexRef.current)} />
    </XPathMappingRowStyled>
  );
};

export default XPathMappingRowComponent;
